import React, { useState } from 'react';
import { AlertTriangle, Loader2, Unlock, User } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { useColaboradoresLiberaveis, useLiberarNotaFiscal, usePreviewLiberacao } from '../hooks';
import { apiErrorMessage, formatCompetencia, formatCurrency } from '../constants';
import { NFModalShell, btnPrimary, btnSecondary } from './NFModalShell';

interface Props {
  onClose: () => void;
  onReleased: (message: string) => void;
}

function mesAtual(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Liberação individual: colaborador PJ + competência. Antes de confirmar
 * mostra o valor que será liberado (salário vigente na competência).
 */
export const LiberarNFModal: React.FC<Props> = ({ onClose, onReleased }) => {
  const [collaboratorId, setCollaboratorId] = useState('');
  const [referenceMonth, setReferenceMonth] = useState(mesAtual());
  const [error, setError] = useState<string | null>(null);

  const { data: colaboradores = [], isLoading: carregandoColaboradores } = useColaboradoresLiberaveis('');
  const preview = usePreviewLiberacao(collaboratorId || undefined, referenceMonth || undefined);
  const liberar = useLiberarNotaFiscal();

  const colaborador = colaboradores.find((c) => c.id === collaboratorId);
  const semSalario = preview.data && preview.data.value === null;
  const jaLiberada = preview.data?.alreadyReleased;
  const podeLiberar = !!preview.data && !semSalario && !jaLiberada && !liberar.isPending;

  async function handleLiberar() {
    setError(null);
    try {
      await liberar.mutateAsync({ collaboratorId, referenceMonth });
      onReleased(`NF de ${formatCompetencia(referenceMonth)} liberada para ${colaborador?.name ?? 'o colaborador'}.`);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível liberar a nota fiscal.'));
    }
  }

  return (
    <NFModalShell
      title="Liberar nota fiscal"
      subtitle="O colaborador só consegue enviar a NF depois da liberação."
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={btnSecondary} disabled={liberar.isPending}>
            Cancelar
          </button>
          <button type="button" onClick={handleLiberar} className={btnPrimary} disabled={!podeLiberar}>
            {liberar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />}
            Liberar
          </button>
        </>
      }
    >
      <AutocompleteSelect
        label={carregandoColaboradores ? 'Carregando colaboradores…' : 'Colaborador PJ *'}
        icon={<User className="h-4 w-4" />}
        options={colaboradores.map((c) => ({ value: c.id, label: c.name, description: c.email }))}
        value={collaboratorId}
        onChange={setCollaboratorId}
      />

      <div>
        <label htmlFor="nf-competencia" className="mb-1 block text-sm font-medium text-gray-700">
          Competência *
        </label>
        <input
          id="nf-competencia"
          type="month"
          value={referenceMonth}
          onChange={(e) => setReferenceMonth(e.target.value)}
          className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {collaboratorId && referenceMonth && (
        <div className="rounded-lg border border-gray-200 p-3 text-sm">
          {preview.isLoading ? (
            <span className="flex items-center gap-2 text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Calculando valor…
            </span>
          ) : preview.isError ? (
            <span className="text-red-700">{apiErrorMessage(preview.error, 'Não foi possível consultar o valor.')}</span>
          ) : jaLiberada ? (
            <span className="flex items-start gap-2 text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              Já existe NF deste colaborador para {formatCompetencia(referenceMonth)}.
            </span>
          ) : semSalario ? (
            <span className="flex items-start gap-2 text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                O colaborador não tem salário vigente em {formatCompetencia(referenceMonth)}. Cadastre o salário em{' '}
                <a href="/usuarios" className="font-medium underline">
                  Usuários
                </a>{' '}
                antes de liberar.
              </span>
            </span>
          ) : preview.data ? (
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Valor a liberar (salário vigente)</span>
              <span className="text-base font-semibold text-gray-900">{formatCurrency(preview.data.value)}</span>
            </div>
          ) : null}
        </div>
      )}

      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
    </NFModalShell>
  );
};
